import type { SemanticMetadata, SemanticType } from "../types/SemanticTypes";

export interface TypographySpec {
  fontSize: number;
  bold?: boolean;
  italic?: boolean;
  alignment?: Word.Alignment | "Left" | "Centered" | "Right" | "Justified";
  styleBuiltIn?: Word.BuiltInStyleName | string;
  lineSpacing?: "single" | "onePointFive";
  spaceBefore?: number;
  spaceAfter?: number;
  // When true, keep inline run formatting such as bold/italic/underline and
  // hyperlinks while still applying the block-level paragraph typography.
  preserveRuns?: boolean;
}

export const BOOK_ANTIQUA = "Book Antiqua";
// Mac Word: Office.js `paragraph.lineSpacing` sets a MULTIPLE rule of value/12,
// not points. 12 -> Multiple 1.0 (single); 18 -> Multiple 1.5.
export const SINGLE_LINE = 12;
export const ONE_HALF_LINE = 18;
// Palatino clones, in preference order, used if Book Antiqua isn't installed.
const FALLBACK_FONTS = ["Palatino Linotype", "Palatino", "Palatino LT STD", "URW Palladio L"];

let resolvedFont: string | null = null;

// An Office.js add-in can't install a system font. Detect (via canvas width
// comparison in the taskpane webview, which shares the OS font collection with
// Word) whether Book Antiqua exists; if not, fall back to a Palatino clone.
export function bodyFont(): string {
  if (resolvedFont) return resolvedFont;
  if (isFontAvailable(BOOK_ANTIQUA)) {
    resolvedFont = BOOK_ANTIQUA;
  } else {
    resolvedFont = FALLBACK_FONTS.find(isFontAvailable) ?? BOOK_ANTIQUA;
  }
  return resolvedFont;
}

export function activeFontIsFallback(): boolean {
  return bodyFont() !== BOOK_ANTIQUA;
}

function isFontAvailable(font: string): boolean {
  if (typeof document === "undefined") return true;
  const sample = "mmmmmmmmmmlliWWWQ";
  const baseFonts = ["monospace", "sans-serif", "serif"];
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return true;
  for (const base of baseFonts) {
    ctx.font = `72px ${base}`;
    const baseWidth = ctx.measureText(sample).width;
    ctx.font = `72px "${font}", ${base}`;
    if (ctx.measureText(sample).width !== baseWidth) return true;
  }
  return false;
}

export const typographyByType = {
  title: { fontSize: 28, bold: true, alignment: "Centered", styleBuiltIn: "Title", lineSpacing: "single", spaceAfter: 12 },
  subtitle: { fontSize: 16, italic: true, alignment: "Centered", styleBuiltIn: "Subtitle", lineSpacing: "single", spaceAfter: 12 },
  author: { fontSize: 11, alignment: "Centered", lineSpacing: "single", spaceBefore: 12, spaceAfter: 0, preserveRuns: true },
  doi: { fontSize: 11, alignment: "Centered", lineSpacing: "single", spaceBefore: 6, spaceAfter: 12, preserveRuns: true },
  abstract_heading: { fontSize: 14, bold: true, alignment: "Left", styleBuiltIn: "Heading2", lineSpacing: "single", spaceAfter: 12 },
  abstract: { fontSize: 11, alignment: "Justified", styleBuiltIn: "Normal", lineSpacing: "onePointFive", spaceAfter: 8, preserveRuns: true },
  keywords_heading: { fontSize: 14, bold: true, alignment: "Left", styleBuiltIn: "Heading2", lineSpacing: "single", spaceAfter: 12 },
  keywords: { fontSize: 11, alignment: "Justified", styleBuiltIn: "Normal", lineSpacing: "onePointFive", spaceAfter: 8, preserveRuns: true },
  references_heading: { fontSize: 14, bold: true, alignment: "Left", styleBuiltIn: "Heading2", lineSpacing: "single", spaceAfter: 12 },
  references: { fontSize: 10, alignment: "Justified", styleBuiltIn: "Normal", lineSpacing: "onePointFive", spaceAfter: 4, preserveRuns: true },
  paragraph: { fontSize: 11, alignment: "Justified", styleBuiltIn: "Normal", lineSpacing: "onePointFive", spaceAfter: 6, preserveRuns: true },
  caption: { fontSize: 10, alignment: "Centered", styleBuiltIn: "Caption", lineSpacing: "single", spaceBefore: 6, spaceAfter: 12, preserveRuns: true },
  figure: { fontSize: 11, alignment: "Centered", styleBuiltIn: "Normal", lineSpacing: "single", spaceBefore: 12, spaceAfter: 12 },
  heading1: { fontSize: 14, bold: true, alignment: "Left", styleBuiltIn: "Heading1", lineSpacing: "single", spaceBefore: 10, spaceAfter: 12 },
  heading2: { fontSize: 13, bold: true, alignment: "Left", styleBuiltIn: "Heading2", lineSpacing: "single", spaceBefore: 8, spaceAfter: 12 },
  heading3: { fontSize: 12, bold: true, alignment: "Left", styleBuiltIn: "Heading3", lineSpacing: "single", spaceBefore: 8, spaceAfter: 12 },
  heading4: { fontSize: 11, bold: true, alignment: "Left", styleBuiltIn: "Heading4", lineSpacing: "single", spaceBefore: 6, spaceAfter: 12 },
  heading5: { fontSize: 11, alignment: "Left", styleBuiltIn: "Heading5", lineSpacing: "single", spaceBefore: 6, spaceAfter: 12 },
  heading6: { fontSize: 11, italic: true, alignment: "Left", styleBuiltIn: "Heading6", lineSpacing: "single", spaceBefore: 6, spaceAfter: 12 },
  footnote: { fontSize: 9, alignment: "Justified", styleBuiltIn: "Normal", lineSpacing: "single", spaceAfter: 2, preserveRuns: true },
  equation: { fontSize: 11, alignment: "Centered", styleBuiltIn: "Normal", lineSpacing: "single", spaceBefore: 8, spaceAfter: 8, preserveRuns: true }
} satisfies Record<string, TypographySpec>;

export function specForMetadata(metadata: Pick<SemanticMetadata, "type" | "level">): TypographySpec {
  if (metadata.type === "title") return typographyByType.title;
  if (metadata.type === "subtitle") return typographyByType.subtitle;
  if (metadata.type === "author") return typographyByType.author;
  if (metadata.type === "doi") return typographyByType.doi;
  if (metadata.type === "abstract") return typographyByType.abstract;
  if (metadata.type === "keywords") return typographyByType.keywords;
  if (metadata.type === "references" || metadata.type === "reference") return typographyByType.references;
  if (metadata.type === "figure_caption" || metadata.type === "table_caption" || metadata.type === "table_note") return typographyByType.caption;
  if (metadata.type === "figure") return typographyByType.figure;
  if (metadata.type === "footnote") return typographyByType.footnote;
  if (metadata.type === "equation") return typographyByType.equation;
  if (metadata.type === "section") {
    if (metadata.level === 1) return typographyByType.heading1;
    if (metadata.level === 2) return typographyByType.heading2;
    if (metadata.level === 3) return typographyByType.heading3;
    if (metadata.level === 4) return typographyByType.heading4;
    if (metadata.level === 5) return typographyByType.heading5;
    if (metadata.level === 6) return typographyByType.heading6;
  }
  return typographyByType.paragraph;
}

export function applyTypographyToRange(range: Word.Range, spec: TypographySpec): void {
  if (!spec.preserveRuns && spec.styleBuiltIn) range.styleBuiltIn = spec.styleBuiltIn as Word.BuiltInStyleName;
  if (!spec.preserveRuns) {
    range.font.name = bodyFont();
    range.font.size = spec.fontSize;
    range.font.bold = spec.bold ?? false;
    range.font.italic = spec.italic ?? false;
    range.font.color = "black";
  }
  const paragraphs = range.paragraphs;
  if (spec.lineSpacing === "onePointFive") {
    paragraphs.space1Pt5();
  } else {
    paragraphs.space1();
  }
  const first = paragraphs.getFirstOrNullObject();
  first.alignment = spec.alignment ?? "Left";
  first.spaceAfter = spec.spaceAfter ?? 0;
  first.spaceBefore = spec.spaceBefore ?? 0;
  const last = paragraphs.getLastOrNullObject();
  last.alignment = spec.alignment ?? "Left";
  last.spaceAfter = spec.spaceAfter ?? 0;
  last.spaceBefore = spec.spaceBefore ?? 0;
}

export async function applyTypographyToRangeDeep(context: Word.RequestContext, range: Word.Range, spec: TypographySpec): Promise<void> {
  applyTypographyToRange(range, spec);
  const paragraphs = range.paragraphs;
  paragraphs.load("items");
  await context.sync();
  for (const paragraph of paragraphs.items) {
    if (!spec.preserveRuns && spec.styleBuiltIn) {
      paragraph.styleBuiltIn = spec.styleBuiltIn as Word.BuiltInStyleName;
    }
    if (!spec.preserveRuns) {
      paragraph.font.name = bodyFont();
      paragraph.font.size = spec.fontSize;
      paragraph.font.bold = spec.bold ?? false;
      paragraph.font.italic = spec.italic ?? false;
      paragraph.font.color = "black";
    }
    paragraph.alignment = spec.alignment ?? "Left";
    paragraph.spaceAfter = spec.spaceAfter ?? 0;
    paragraph.spaceBefore = spec.spaceBefore ?? 0;
    // Mac Word: lineSpacing is a Multiple rule of value/12. 18 -> 1.5; 12 -> single.
    paragraph.lineSpacing = spec.lineSpacing === "onePointFive" ? ONE_HALF_LINE : SINGLE_LINE;
  }
}

export function labelForHeading(type: Extract<SemanticType, "abstract" | "keywords" | "references">): string {
  if (type === "abstract") return "Abstract";
  if (type === "keywords") return "Keywords";
  return "References";
}
